import moment from 'moment';
import { createSelector } from 'reselect';

import { RootState } from 'src/config/rootStore';
import { CoachingVisitState } from 'src/generated/webapp_gql';
import { getMostRecentAssessmentDate } from 'src/modules/assessments/selectors/assessmentsSelectors';
import { FetchStatus } from 'src/shared/models/shared';

import { PRE_SESSION_ASSESSMENTS_DISPLAYED_DATE_STORAGE_KEY } from '../../assessments/pages/PreSessionAssessmentsIntroPage';
import {
    DEFAULT_COACHING_SESSION_DURATION,
    DEFAULT_THERAPY_SESSION_DURATION,
} from '../models/videoSessions';

export const getVideoSessions = (state: RootState) => state.videoSessions.sessions;

export const getTherapySessions = createSelector(getVideoSessions, sessions =>
    sessions.filter(session => session?.isTherapy === true),
);

export const getSortedVideoSessions = createSelector(getVideoSessions, coachingSessions =>
    [...coachingSessions].sort((a, b) => moment(a.scheduledTime).diff(moment(b.scheduledTime))),
);

export const getSortedVideoSessionsLatestFirst = createSelector(
    getVideoSessions,
    coachingSessions =>
        [...coachingSessions].sort((a, b) => moment(b.scheduledTime).diff(moment(a.scheduledTime))),
);

export const getSessionUtilizationInfo = (state: RootState) =>
    state.videoSessions.sessionUtilizationInfo;

export const getUpcomingVideoSessions = createSelector(getSortedVideoSessions, coachingSessions =>
    coachingSessions.filter(session => {
        const scheduledTime = moment(session.scheduledTime);
        const secondsDiff = scheduledTime.diff(moment(), 'seconds');

        const defaultDuration = session.isTherapy
            ? DEFAULT_THERAPY_SESSION_DURATION
            : DEFAULT_COACHING_SESSION_DURATION;

        const sessionDuration = session?.scheduledDuration
            ? session.scheduledDuration * 60 // session.scheduledDuration is in minutes
            : defaultDuration;

        return (
            session.state === CoachingVisitState.Scheduled && secondsDiff >= sessionDuration * -1
        );
    }),
);

export const getPendingPaymentVideoSessions = createSelector(getTherapySessions, coachingSessions =>
    [...coachingSessions]
        .sort((a, b) => moment(a.scheduledTime).diff(moment(b.scheduledTime)))
        .filter(session => session.state === CoachingVisitState.PendingPayment),
);

export const getPastVideoSessions = createSelector(
    getSortedVideoSessionsLatestFirst,
    coachingSessions =>
        coachingSessions.filter(session => {
            const scheduledTime = moment(session.scheduledTime);
            const secondsDiff = moment().diff(scheduledTime, 'seconds');

            const defaultDuration = session.isTherapy
                ? DEFAULT_THERAPY_SESSION_DURATION
                : DEFAULT_COACHING_SESSION_DURATION;

            const sessionDuration = session?.scheduledDuration
                ? session.scheduledDuration * 60 // session.scheduledDuration is in minutes
                : defaultDuration;

            return secondsDiff >= sessionDuration && session.state !== CoachingVisitState.Cancelled;
        }),
);

export const isSessionFetchingCompleted = (state: RootState) =>
    state.videoSessions.fetchStatus === FetchStatus.Completed;

export const shouldShowPreSessionAssessments = createSelector(
    getUpcomingVideoSessions,
    getMostRecentAssessmentDate,
    (sessions, assessmentCompletionDate): string | false => {
        const rightNow = moment();

        const sessionWithin24Hr = sessions.find(
            session => moment(session.scheduledTime).diff(rightNow, 'h') < 25,
        );

        if (!sessionWithin24Hr) {
            return false;
        }

        const hoursDiffSinceAssessmentCompletion = rightNow.diff(
            moment(assessmentCompletionDate),
            'h',
        );

        if (hoursDiffSinceAssessmentCompletion < 48) {
            return false;
        }

        const dateWhenUserSawPreSessionAssessments = localStorage.getItem(
            PRE_SESSION_ASSESSMENTS_DISPLAYED_DATE_STORAGE_KEY,
        );

        if (!dateWhenUserSawPreSessionAssessments) {
            return sessionWithin24Hr.uuid;
        }

        const hoursDiffSinceUserSawPreSessionAssessments = rightNow.diff(
            moment(dateWhenUserSawPreSessionAssessments),
            'h',
        );

        return hoursDiffSinceUserSawPreSessionAssessments >= 48 ? sessionWithin24Hr.uuid : false;
    },
);
